import { Money } from '@Types/product/Money';
import { Log } from './errorLogger';
import { StringHelpers } from './stringHelpers';

export class CurrencyHelpers {
  static getDollarsValue = (value: Money): number => {
    return value ? parseFloat((value.centAmount / 100).toFixed(2)) : -1;
  };

  static formatToMoney = function (costInDollars: number): Money {
    return {
      centAmount: costInDollars * 100,
      currencyCode: 'GBP',
      fractionDigits: 2,
    };
  };
  static formatToMoneyWithCurrency = function (costInDollars: number, currency: string, minimumFractionDigits?: number): Money {
    return {
      centAmount: costInDollars * 100,
      currencyCode: currency,
      fractionDigits: 2,
      minimumFractionDigits: minimumFractionDigits ?? 4
    };
  };

  private static formatNumberForCurrency = function (costInCents: number) {
    return CurrencyHelpers.formatMoneyCurrency({
      centAmount: costInCents,
      currencyCode: 'GBP',
      fractionDigits: 2,
    });
  };

  private static formatStringForCurrency = function (costInCents: string) {
    if (!StringHelpers.isNumeric(costInCents)) {
      Log.error(`Value (${costInCents}) passed for currency formatting cannot be parsed to a number`);
      return '';
    }
    return CurrencyHelpers.formatNumberForCurrency(parseInt(costInCents, 10));
  };

  private static formatMoneyCurrency = function (price: Money) {
    return Intl.NumberFormat('de-DE', { style: 'currency', currency: price?.currencyCode ?? 'EUR', minimumFractionDigits: price?.minimumFractionDigits ?? 4 }).format(
      (price?.preciseAmount ?? (price?.centAmount ?? 0)) / Math.pow(10, price?.fractionDigits ?? 2),
    );
  };

  /**
   * formatForCurrency formats a string of number into a cost representation. If
   * passing a string it must be numeric only.
   */
  static formatForCurrency = (costInCents: string | number | Money) =>
    typeof costInCents === 'string'
      ? CurrencyHelpers.formatStringForCurrency(costInCents)
      : typeof costInCents === 'number'
      ? CurrencyHelpers.formatNumberForCurrency(costInCents)
      : CurrencyHelpers.formatMoneyCurrency(costInCents);

  static addCurrency: (value1?: Money, value2?: Money) => Money = (value1?: Money, value2?: Money) => {
    if (!value1) value1 = { centAmount: 0 };
    if (!value2) value2 = { centAmount: 0 };
    if (value1.fractionDigits !== value2.fractionDigits && value1.fractionDigits && value2.fractionDigits) {
      Log.warn(
        `Money with different fraction codes passed to addCurrency, value returned will be innacurate. ` +
          `Value 1: ${value1.fractionDigits}, value 2: ${value2.fractionDigits}`,
      );
    }
    if (value1.currencyCode !== value2.currencyCode && value1.currencyCode && value2.currencyCode) {
      Log.warn(
        `Money with different currency codes passed to addCurrency, value returned will be innacurate. ` +
          `Value 1: ${value1.currencyCode}, value 2: ${value2.currencyCode}`,
      );
    }
    return {
      fractionDigits: value1.fractionDigits || value2.fractionDigits,
      centAmount: value1.centAmount + value2.centAmount,
      currencyCode: value1.currencyCode || value2.currencyCode,
      preciseAmount: value1.preciseAmount + value2.preciseAmount,
    };
  };

  static subtractCurrency: (value1: Money, value2: Money) => Money = (value1: Money, value2: Money) => {
    if (value1.fractionDigits !== value2.fractionDigits) {
      Log.warn(
        `Money with different fraction codes passed to addCurrency, value returned will be innacurate. ` +
          `Value 1: ${value1.fractionDigits}, value 2: ${value2.fractionDigits}`,
      );
    }
    if (value1.currencyCode !== value2.currencyCode) {
      Log.warn(
        `Money with different currency codes passed to addCurrency, value returned will be innacurate. ` +
          `Value 1: ${value1.currencyCode}, value 2: ${value2.currencyCode}`,
      );
    }
    return {
      fractionDigits: value1.fractionDigits || value2.fractionDigits,
      centAmount: value1.centAmount - value2.centAmount,
      currencyCode: value1.currencyCode || value2.currencyCode,
    };
  };

  static multiplyCurrency: (value: Money, numberOfItems: number) => Money = (value: Money, numberOfItems: number) => ({
    fractionDigits: value.fractionDigits,
    centAmount: value.centAmount * numberOfItems,
    currencyCode: value.currencyCode,
  });
}
